/**
 * Service for managing starred leads functionality
 * Handles starring/unstarring leads and retrieving starred leads for users
 */

import {
  collection,
  doc,
  addDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  StarDocument,
  StarredLead,
  StarLeadRequest,
  UnstarLeadRequest,
  UpdateStarRequest
} from '../types/Star';
import { COLLECTION_NAMES } from '../utils/constants';

export class StarService {
  private static instance: StarService;
  private starCache = new Map<string, StarredLead[]>(); // userUid -> starred leads
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private lastCacheFetch = new Map<string, number>(); // userUid -> timestamp

  private constructor() {}

  static getInstance(): StarService {
    if (!StarService.instance) {
      StarService.instance = new StarService();
    }
    return StarService.instance;
  }

  /**
   * Star a lead for a user
   */
  async starLead(request: StarLeadRequest): Promise<StarredLead> {
    try {
      // Check if lead is already starred by this user
      const existingStar = await this.isLeadStarred(request.leadId, request.userUid);
      if (existingStar) {
        // If already starred, get the existing star data and return it
        // This prevents the error and ensures UI consistency
        console.log(`⚠️ Lead ${request.leadId} is already starred for user ${request.userUid}, returning existing star`);

        // Get the existing star document
        const starsRef = collection(db, COLLECTION_NAMES.STARS);
        const q = query(
          starsRef,
          where('leadId', '==', request.leadId),
          where('userUid', '==', request.userUid),
          limit(1)
        );

        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const starDoc = querySnapshot.docs[0];
          const data = starDoc.data() as StarDocument;

          const starredLead: StarredLead = {
            leadId: data.leadId,
            starredAt: data.starredAt,
            notes: data.notes,
            priority: data.priority,
            starId: starDoc.id
          };

          // Clear cache to force refresh and ensure UI consistency
          this.clearUserCache(request.userUid);

          return starredLead;
        }
      }

      const starDoc: any = {
        leadId: request.leadId,
        userUid: request.userUid,
        starredAt: new Date().toISOString(),
        priority: request.priority || 'medium'
      };

      // Only add notes if it's provided (Firestore doesn't allow undefined)
      if (request.notes !== undefined) {
        starDoc.notes = request.notes;
      }

      // Add to stars collection
      const docRef = await addDoc(collection(db, COLLECTION_NAMES.STARS), starDoc);

      const starredLead: StarredLead = {
        leadId: request.leadId,
        starredAt: starDoc.starredAt,
        notes: starDoc.notes,
        priority: starDoc.priority,
        starId: docRef.id
      };

      // Clear cache to force refresh
      this.clearUserCache(request.userUid);

      console.log(`✅ Starred lead ${request.leadId} for user ${request.userUid}`);
      return starredLead;
    } catch (error) {
      console.error('❌ Error starring lead:', error);
      throw new Error('Failed to star lead');
    }
  }

  /**
   * Unstar a lead for a user
   */
  async unstarLead(request: UnstarLeadRequest): Promise<void> {
    try {
      console.log(`🔄 Attempting to unstar lead ${request.leadId} for user ${request.userUid}`);

      // Find the star document
      const starsRef = collection(db, COLLECTION_NAMES.STARS);
      const q = query(
        starsRef,
        where('leadId', '==', request.leadId),
        where('userUid', '==', request.userUid)
      );

      const querySnapshot = await getDocs(q);
      console.log(`🔍 Found ${querySnapshot.size} star documents for lead ${request.leadId}`);

      if (querySnapshot.empty) {
        console.error(`❌ No star found for lead ${request.leadId} and user ${request.userUid}`);
        throw new Error('Star not found');
      }

      // Delete the star document
      const starDoc = querySnapshot.docs[0];
      console.log(`🗑️ Deleting star document ${starDoc.id} for lead ${request.leadId}`);
      await deleteDoc(doc(db, COLLECTION_NAMES.STARS, starDoc.id));

      // Clear cache to force refresh
      this.clearUserCache(request.userUid);
      console.log(`🧹 Cleared cache for user ${request.userUid}`);

      console.log(`✅ Unstarred lead ${request.leadId} for user ${request.userUid}`);
    } catch (error) {
      console.error('❌ Error unstarring lead:', error);
      throw new Error('Failed to unstar lead');
    }
  }

  /**
   * Update star metadata (notes, priority)
   */
  async updateStar(request: UpdateStarRequest): Promise<void> {
    try {
      const updateData: any = {};

      if (request.notes !== undefined) {
        updateData.notes = request.notes;
      }

      if (request.priority !== undefined) {
        updateData.priority = request.priority;
      }

      // Only update if there's actual data to update
      if (Object.keys(updateData).length > 0) {
        await updateDoc(doc(db, COLLECTION_NAMES.STARS, request.starId), updateData);
        console.log(`✅ Updated star ${request.starId}`);
      }
    } catch (error) {
      console.error('❌ Error updating star:', error);
      throw new Error('Failed to update star');
    }
  }

  /**
   * Get all starred leads for a user
   */
  async getStarredLeads(userUid: string, useCache: boolean = true): Promise<StarredLead[]> {
    try {
      // Check cache first
      if (useCache && this.starCache.has(userUid)) {
        const lastFetch = this.lastCacheFetch.get(userUid) || 0;
        if (Date.now() - lastFetch < this.CACHE_DURATION) {
          return this.starCache.get(userUid)!;
        }
      }

      const starsRef = collection(db, COLLECTION_NAMES.STARS);
      const q = query(
        starsRef,
        where('userUid', '==', userUid),
        orderBy('starredAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const starredLeads: StarredLead[] = [];

      querySnapshot.forEach(doc => {
        const data = doc.data() as StarDocument;
        starredLeads.push({
          leadId: data.leadId,
          starredAt: data.starredAt,
          notes: data.notes,
          priority: data.priority,
          starId: doc.id
        });
      });

      // Update cache
      this.starCache.set(userUid, starredLeads);
      this.lastCacheFetch.set(userUid, Date.now());

      return starredLeads;
    } catch (error) {
      console.error('❌ Error getting starred leads:', error);
      return [];
    }
  }

  /**
   * Sync starred status for a specific lead - useful for fixing UI inconsistencies
   */
  async syncStarredStatus(leadId: string, userUid: string): Promise<StarredLead | null> {
    try {
      console.log(`🔄 Syncing starred status for lead ${leadId} and user ${userUid}`);

      // Clear cache to force fresh data
      this.clearUserCache(userUid);

      // Get the current starred status
      const starredLead = await this.getStarredLead(leadId, userUid);

      if (starredLead) {
        console.log(`✅ Lead ${leadId} is starred for user ${userUid}:`, starredLead);
      } else {
        console.log(`❌ Lead ${leadId} is not starred for user ${userUid}`);
      }

      return starredLead;
    } catch (error) {
      console.error('❌ Error syncing starred status:', error);
      return null;
    }
  }

  /**
   * Get a single starred lead by leadId and userUid
   */
  async getStarredLead(leadId: string, userUid: string): Promise<StarredLead | null> {
    try {
      const starsRef = collection(db, COLLECTION_NAMES.STARS);
      const q = query(
        starsRef,
        where('leadId', '==', leadId),
        where('userUid', '==', userUid),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        return null;
      }

      const starDoc = querySnapshot.docs[0];
      const data = starDoc.data() as StarDocument;

      return {
        leadId: data.leadId,
        starredAt: data.starredAt,
        notes: data.notes,
        priority: data.priority,
        starId: starDoc.id
      };
    } catch (error) {
      console.error('❌ Error getting starred lead:', error);
      return null;
    }
  }

  /**
   * Check if a lead is starred by a user
   */
  async isLeadStarred(leadId: string, userUid: string): Promise<boolean> {
    try {
      const starsRef = collection(db, COLLECTION_NAMES.STARS);
      const q = query(
        starsRef,
        where('leadId', '==', leadId),
        where('userUid', '==', userUid),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('❌ Error checking if lead is starred:', error);
      return false;
    }
  }

  /**
   * Get starred lead IDs for a user (lightweight version)
   */
  async getStarredLeadIds(userUid: string): Promise<string[]> {
    try {
      const starredLeads = await this.getStarredLeads(userUid);
      return starredLeads.map(star => star.leadId);
    } catch (error) {
      console.error('❌ Error getting starred lead IDs:', error);
      return [];
    }
  }

  /**
   * Get star count for a lead (for future analytics)
   */
  async getLeadStarCount(leadId: string): Promise<number> {
    try {
      const starsRef = collection(db, COLLECTION_NAMES.STARS);
      const q = query(
        starsRef,
        where('leadId', '==', leadId)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error('❌ Error getting lead star count:', error);
      return 0;
    }
  }

  /**
   * Clear cache for a specific user
   */
  private clearUserCache(userUid: string): void {
    this.starCache.delete(userUid);
    this.lastCacheFetch.delete(userUid);
  }

  /**
   * Clear all cache (for logout, etc.)
   */
  clearAllCache(): void {
    this.starCache.clear();
    this.lastCacheFetch.clear();
  }
}

// Export singleton instance
export const starService = StarService.getInstance();